import InventoryHolderTypeTitle from './enums/InventoryHolderTypeTitle';
import {
    EMPTY_VALUE,
    SYSTEM_ATTRIBUTE,
    extraAttributes,
    itemCityName,
    systemAttributeValue,
} from './inventoryAttributes';

/**
 * Содержание карточки оборудования: что и в каком блоке показывать.
 *
 * Отдельным модулем, а не вычисляемыми свойствами компонента: карточка тянет за собой
 * inventory-api → http.js → OIDC, и в тестах не монтируется. Решения о составе блоков —
 * самое хрупкое в карточке, и проверяться они должны, а не считаться очевидными.
 *
 * ОБЩЕЕ ПРАВИЛО ПУСТОТЫ. Пустое поле в блок не попадает, а блок без полей не показывается
 * вовсе. Прочерк сам по себе ничего не сообщает: строка «Гарантия до —» занимает столько же
 * места, сколько заполненная, и читается как данные, которых нет. Исключение одно —
 * ответственный: у него пустое состояние осмысленное и названо словами, см. holderInfo().
 *
 * Новых полей здесь не появляется: всё считается из того, что уже отдаёт API.
 */

/**
 * Пусто ли значение. Прочерк — тоже пусто: его подставляют читатели атрибутов,
 * когда значения нет.
 *
 * @param {*} value
 * @returns {boolean}
 */
export function isBlank(value) {
    if (value === null || value === undefined) {
        return true;
    }

    const text = String(value).trim();

    return text === '' || text === EMPTY_VALUE;
}

/**
 * Поле блока или null, если показывать нечего.
 *
 * @param {string} label
 * @param {*} value
 * @param {string} [note] Приписка к значению — например, «истекла» у гарантии.
 * @returns {Object|null}
 */
function fact(label, value, note) {
    if (isBlank(value)) {
        return null;
    }

    return note ? { label, value: String(value), note } : { label, value: String(value) };
}

/**
 * @param {Array} list
 * @returns {Array}
 */
function present(list) {
    return list.filter(Boolean);
}

/**
 * Город единицы техники для паспорта предмета.
 *
 * Берётся тем же способом, что и в списке: сначала собственная колонка предмета, потом
 * старый атрибут. На доступ это не влияет никак — доступ всегда по city_id.
 *
 * @param {Object|null} item
 * @returns {string} Пустая строка, если города нет.
 */
export function headerCity(item) {
    const city = itemCityName(item);

    return isBlank(city) ? '' : String(city);
}

/**
 * Паспорт предмета: то, по чему единицу техники опознают и сверяют с учётом.
 *
 * ЭТО ЕДИНСТВЕННОЕ МЕСТО, ГДЕ ЭТИ ПОЛЯ ЕСТЬ. Номер позиции, город и серийный номер стояли
 * когда-то и в шапке — от этой второй копии в шапке отказались: мелкие служебные строки
 * под названием делали её плотной, но не сильной. Читают эти поля здесь, рядом с наклейкой
 * на корпусе и со строкой в учётной системе. Наименование повторяет заголовок намеренно:
 * при сверке выхватывать его глазами из шапки неудобно.
 *
 * Поля — только те, что у предмета действительно есть. Ни бренда, ни модели, ни отдельного
 * инвентарного номера в модели нет, и выдумывать их карточка не станет: серийный номер
 * и юр. лицо приходят атрибутами, остальное — собственные колонки предмета.
 *
 * @param {Object|null} item
 * @returns {Array}
 */
export function assetSummary(item) {
    if (!item) {
        return [];
    }

    return present([
        fact('Наименование', item.name),
        fact('№ позиции', item.id),
        fact('Серийный номер', systemAttributeValue(item, SYSTEM_ATTRIBUTE.SERIAL_NUMBER)),
        fact('Город', headerCity(item)),
        fact('Юр. лицо', systemAttributeValue(item, SYSTEM_ATTRIBUTE.LEGAL_ENTITY)),
    ]);
}

/**
 * Атрибуты, которым в карточке нужна вся ширина.
 *
 * Назначение и комментарий — свободный текст в несколько строк: в сетке реквизитов такое
 * значение либо обрезается, либо растягивает соседей. Поэтому им отдельные блоки.
 *
 * ЭТО НЕ СИСТЕМНЫЕ ПОЛЯ. В модели предмета их нет; это обычные пользовательские атрибуты,
 * которые где-то завели с такими названиями. Списки написаний нарочно узкие: угадывать
 * «Примечание», «Описание» и прочее — значит однажды вывести широким блоком чужой
 * справочник. Нет атрибута — нет и блока, заглушку карточка не рисует.
 */
const WIDE_ATTRIBUTES = [
    { label: 'Назначение', spellings: ['назначение'] },
    { label: 'Комментарий', spellings: ['комментарий', 'комментарии'] },
];

/**
 * Первый непустой атрибут с одним из перечисленных названий.
 *
 * @param {Object|null} item
 * @param {string[]} spellings Нормализованные написания по убыванию приоритета.
 * @returns {Object|null}
 */
function findAttributeByType(item, spellings) {
    const attributes = Array.isArray(item?.attributes) ? item.attributes : [];

    for (const spelling of spellings) {
        const found = attributes.find(
            (attribute) =>
                String(attribute?.type ?? '')
                    .trim()
                    .toLowerCase() === spelling && !isBlank(attribute?.value),
        );

        if (found) {
            return found;
        }
    }

    return null;
}

/**
 * Широкие блоки карточки — только те, под которые у предмета реально есть атрибут.
 *
 * @param {Object|null} item
 * @returns {Array} [{ id, label, value }]
 */
export function wideAttributeBlocks(item) {
    if (!item) {
        return [];
    }

    return present(
        WIDE_ATTRIBUTES.map(({ label, spellings }) => {
            const attribute = findAttributeByType(item, spellings);

            return attribute ? { id: attribute.id, label, value: String(attribute.value) } : null;
        }),
    );
}

/**
 * Прочие атрибуты — для компактной сетки «Дополнительные данные».
 *
 * Обёртка над extraAttributes(), а не замена ей: правило «не прятать то, что не показано
 * выше» живёт там и остаётся единственным. Здесь из результата убирается только то,
 * что карточка уже вывела широкими блоками.
 *
 * @param {Object|null} item
 * @returns {Array}
 */
export function extraFacts(item) {
    const usedIds = wideAttributeBlocks(item).map((block) => block.id);

    return extraAttributes(item).filter((attribute) => usedIds.indexOf(attribute.id) === -1);
}

/**
 * Кто держит оборудование.
 *
 * Держатель бывает двух видов, и они взаимоисключающие: сотрудник Service Desk
 * (responsible_person) или запись справочника (holder). Вид выводится из того, какая
 * ссылка непустая, — так же, как в форме и на бэкенде.
 *
 * @param {Object|null} item
 * @returns {{assigned: boolean, name: string, kind: string, department: string, inactive: boolean}}
 */
export function holderInfo(item) {
    const empty = { assigned: false, name: 'Не назначен', kind: '', department: '', inactive: false };

    if (!item) {
        return empty;
    }

    if (item.responsible_person) {
        return {
            assigned: true,
            name: item.responsible_person.displayName || 'Не назначен',
            kind: 'Сотрудник Service Desk',
            department: item.responsible_person.department || '',
            inactive: false,
        };
    }

    if (item.holder) {
        const kind = InventoryHolderTypeTitle[item.holder.type] ?? item.holder.type;

        return {
            assigned: true,
            name: item.holder.full_name || 'Не назначен',
            kind: kind ? String(kind) : '',
            department: '',
            // Погашенную запись справочника с предмета не снимают автоматически, и в карточке
            // это должно быть видно: иначе непонятно, почему её нет в списке выбора.
            inactive: item.holder.active === false,
        };
    }

    return empty;
}

/**
 * Инициалы держателя для аватара.
 *
 * Не новые данные, а то же имя в одну-две буквы: аватар держит левый край блока и делает
 * ответственного заметным с первого взгляда. Имени нет — нет и инициалов: кружок с «?»
 * карточка не рисует.
 *
 * @param {string} name
 * @returns {string}
 */
export function holderInitials(name) {
    const words = String(name ?? '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    return words
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join('');
}

/**
 * Сроки владения. Обе даты необязательные и показываются только заполненными.
 *
 * @param {Object|null} item
 * @param {Function} formatDate Форматирование даты — то же, что во всей карточке.
 * @returns {Array}
 */
export function usageFacts(item, formatDate) {
    if (!item) {
        return [];
    }

    const holder = holderInfo(item);

    return present([
        fact('Подразделение', holder.department),
        fact('Владеет с', formatDate(item.holder_expected_at)),
        fact('Вернуть до', formatDate(item.holder_until_at)),
    ]);
}

/**
 * Истекла ли гарантия.
 *
 * Это чтение уже имеющейся даты, а не новый расчёт: сравнивается warranty_expire_at
 * с сегодняшним днём. Сам срок и правила гарантии остаются как есть.
 *
 * @param {Object|null} item
 * @param {Date} [now]
 * @returns {boolean}
 */
export function isWarrantyExpired(item, now = new Date()) {
    if (!item || isBlank(item.warranty_expire_at)) {
        return false;
    }

    const expiresAt = new Date(item.warranty_expire_at);

    if (Number.isNaN(expiresAt.getTime())) {
        return false;
    }

    // Сравнение по дню: гарантия действует до конца указанной даты.
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const expiryDay = new Date(expiresAt.getFullYear(), expiresAt.getMonth(), expiresAt.getDate());

    return expiryDay < today;
}

/**
 * Учётные и поставочные реквизиты.
 *
 * Стоимость показывается как приходит: своего формата денег в проекте нет, и выдумывать
 * его здесь — не задача карточки.
 *
 * @param {Object|null} item
 * @param {Function} formatDate
 * @param {Date} [now]
 * @returns {Array}
 */
export function supplyFacts(item, formatDate, now = new Date()) {
    if (!item) {
        return [];
    }

    return present([
        fact('Стоимость', item.cost),
        fact('Дата поставки', formatDate(item.delivered_at)),
        fact('Гарантия до', formatDate(item.warranty_expire_at), isWarrantyExpired(item, now) ? 'истекла' : undefined),
    ]);
}
